import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchBroadcastsPaginated } from '@/lib/api/email-flows';
import { QUERY_KEYS, SENTRY_FEATURES } from '@/lib/constants';
import { FlowSummary, PaginatedFlowsResponse } from '@/lib/api/email-flows/types';

const PER_PAGE = 10; // Start small for testing

/**
 * Hook to fetch user-triggered flows (broadcasts) with pagination
 * Uses infinite query to accumulate pages with "Show More" pattern
 */
export const useBroadcasts = () => {
    const query = useInfiniteQuery<PaginatedFlowsResponse, Error>({
        queryKey: [QUERY_KEYS.BROADCASTS],
        queryFn: ({ pageParam }) =>
            fetchBroadcastsPaginated({
                page: pageParam as number,
                per_page: PER_PAGE,
            }),
        initialPageParam: 1,
        getNextPageParam: lastPage => {
            return lastPage.has_more ? lastPage.page + 1 : undefined;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        meta: {
            error: { sentryMessage: '[EMAIL_FLOWS] failed-to-fetch-broadcasts', feature: SENTRY_FEATURES.EMAIL_FLOWS },
        },
    });

    // Flatten all pages into single array
    const broadcasts: FlowSummary[] = query.data?.pages.flatMap(page => page.flows) ?? [];
    const total = query.data?.pages[0]?.total ?? 0;

    return {
        broadcasts,
        total,
        isLoading: query.isLoading,
        isFetchingNextPage: query.isFetchingNextPage,
        hasNextPage: query.hasNextPage ?? false,
        fetchNextPage: query.fetchNextPage,
        error: query.error,
        refetch: query.refetch,
    };
};
